import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginForm } from '../../components/Login/LoginForm';
import { TenantUnavailablePanel } from '../../components/Login/TenantUnavailablePanel';
import { useAuth } from '../../contexts/AuthContext';
import { hasDevTenantContext, isLocalDevHost } from '../../services/devTenantContext';
import { hasInvalidRouteIdSegment } from '../../utils/documentRouteUtils';
import {
  isPostLoginRedirectExcluded,
  tenantHasAnyLicensedModule,
  SUPPORT_PATH,
} from '../../utils/loginRedirectUtils';
import { licenseService } from '../../services/licenseService';
import { resolvePostLoginPath } from '../../utils/postLoginNavigation';
import '../../App.css';

const POST_LOGIN_DEFAULT = '/home';

export const LoginPage: React.FC = () => {
  const { signIn, tenantBootstrap, publicTenant } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (tenantBootstrap.kind !== 'success')
    {
      return;
    }

    const moduleCodes = publicTenant?.moduleCodes;

    if (!tenantHasAnyLicensedModule(moduleCodes))
    {
      navigate(SUPPORT_PATH, { replace: true });
    }
  }, [tenantBootstrap.kind, publicTenant, navigate]);

  const handleLogin = async (email: string, password: string) => {
    try {
      await signIn(email, password);

      let explicitTarget: string | null = null;
      const params = new URLSearchParams(location.search);
      const returnUrl = params.get('returnUrl');
      if (returnUrl && returnUrl.startsWith('/'))
      {
        try {
          const decoded = decodeURIComponent(returnUrl);
          if (
            decoded.startsWith('/') &&
            !hasInvalidRouteIdSegment(decoded) &&
            !isPostLoginRedirectExcluded(decoded)
          ) {
            explicitTarget = decoded;
          }
        } catch {
          /* URL inválida: segue fluxo padrão */
        }
      }

      if (!explicitTarget) {
        const fromState = location.state?.from as
          | { pathname?: string; search?: string; hash?: string }
          | undefined;
        if (fromState && typeof fromState.pathname === 'string' && fromState.pathname.startsWith('/'))
        {
          const candidate = `${fromState.pathname}${fromState.search ?? ''}${fromState.hash ?? ''}`;
          if (!isPostLoginRedirectExcluded(candidate)) {
            explicitTarget = candidate;
          }
        }
      }

      let license = null;
      try {
        license = await licenseService.fetchMyLicense();
        licenseService.cacheLicense(license);
      } catch {
        license = null;
      }

      let userContext: { isAdmin?: boolean; roleCode?: string } | null = null;
      const storedUser = sessionStorage.getItem('@KnowInDoc:user');
      if (storedUser)
      {
        try {
          const parsed = JSON.parse(storedUser) as { isAdmin?: boolean; role?: { code?: string } };
          userContext = {
            isAdmin: parsed.isAdmin,
            roleCode: parsed.role?.code,
          };
        } catch {
          userContext = null;
        }
      }

      const target = resolvePostLoginPath(license, userContext, explicitTarget);

      if (hasInvalidRouteIdSegment(target)) {
        navigate(POST_LOGIN_DEFAULT, { replace: true });
        return true;
      }

      navigate(target, { replace: true });
      return true;
    } catch (error) {
      if (error instanceof Error && error.message.trim()) {
        throw error;
      }
      throw new Error('Credenciais inválidas');
    }
  };

  const renderMain = () => {
    if (tenantBootstrap.kind === 'loading') {
      return (
        <>
          <h2 className="login-greeting">Aguarde</h2>
          <p className="login-subtitle">Verificando o endereço do ambiente…</p>
          <div className="flex justify-center py-10" aria-live="polite">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          </div>
        </>
      );
    }

    if (tenantBootstrap.kind === 'not_found') {
      const localhostSemContextoDev =
        isLocalDevHost() && !hasDevTenantContext();
      return (
        <TenantUnavailablePanel
          variant={localhostSemContextoDev ? 'not_found_localhost' : 'not_found'}
        />
      );
    }

    if (tenantBootstrap.kind === 'unavailable') {
      return <TenantUnavailablePanel variant="unavailable" />;
    }

    return (
      <>
        <h2 className="login-greeting">Olá! :)</h2>
        <p className="login-subtitle">Para começar, faça o login na sua conta:</p>
        <LoginForm onSubmit={handleLogin} />
      </>
    );
  };

  return (
    <div className="login-page-container">
      <div className="login-logo-top">
        <span className="logo-box">b</span>
        <span className="logo-text">Know inDoc</span>
      </div>
      <div className="login-left">{renderMain()}</div>
      <div className="login-right" />
    </div>
  );
};
